import { MapPin, Calendar, Users, ArrowRight, Mountain, Palmtree, Waves, Compass, Hotel, Plane, Car, Binoculars, Building2 } from 'lucide-react';
import { Link } from 'react-router-dom';

const CHILE_IMAGE = 'https://images.unsplash.com/photo-1558517286-6b7b81953cb5';
const AFRICA_IMAGE = 'https://images.unsplash.com/photo-1516026672322-bc52d61a55d5';
const DUBAI_IMAGE = 'https://images.unsplash.com/photo-1512453979798-5ea266f8880c';
const TANZANIA_IMAGE = 'https://images.unsplash.com/photo-1564101160531-4838e8a5f4e7';
const ZANZIBAR_IMAGE = 'https://images.unsplash.com/photo-1621583628955-42fbc37bf424';
const MAURITIUS_IMAGE = 'https://images.unsplash.com/photo-1582574643306-d00ea3f7d49b';

const destinations = [
  {
    id: 1, name: 'Chile',
    description: 'Explore o deserto do Atacama, os Andes nevados, esquiar no Valle Nevado ou em Farellones, ou paisagens de Portillo.',
    image: CHILE_IMAGE, duration: '10 dias', groupSize: '2 pessoas', price: 'A partir de R$ 8.500',
    icon: Mountain, iconColor: 'text-blue-600', detailPage: '/chile',
  },
  {
    id: 2, name: 'África do Sul',
    description: 'Viva a experiência de um safari de luxo na savana africana. Encontre leões, girafas e elefantes em seu habitat natural.',
    image: AFRICA_IMAGE, duration: '10 dias', groupSize: '2 pessoas', price: 'A partir de R$ 15.000,00',
    icon: Palmtree, iconColor: 'text-orange-600', featured: true, detailPage: '/south-africa',
  },
  {
    id: 3, name: 'Dubai',
    description: 'Descubra o luxo e a modernidade de Dubai. Visite o Burj Khalifa, faça compras no Dubai Mall, explore o deserto em safari de camelo e desfrute de praias de classe mundial.',
    image: DUBAI_IMAGE, duration: '8 dias', groupSize: '1 pessoa', price: 'A partir de R$ 10.000',
    icon: Building2, iconColor: 'text-yellow-600', detailPage: '/dubai',
  },
  {
    id: 4, name: 'Tanzânia',
    description: 'Viva a experiência de um safari de luxo na Tanzânia continental. Explore o Serengeti, o Ngorongoro e encontre a vida selvagem em seu habitat natural.',
    image: TANZANIA_IMAGE, duration: '7 dias', groupSize: '1 pessoa', price: 'A partir de R$ 25.000,00',
    icon: Compass, iconColor: 'text-emerald-600', detailPage: '/tanzania',
  },
  {
    id: 5, name: 'Zanzibar',
    description: 'Descubra as ilhas paradisíacas de Zanzibar com praias de areia branca, águas cristalinas e a famosa Stone Town com sua arquitetura histórica.',
    image: ZANZIBAR_IMAGE, duration: '4 dias', groupSize: '1 pessoa', price: 'A partir de R$ 15.000,00',
    icon: Palmtree, iconColor: 'text-blue-600', detailPage: '/zanzibar',
  },
  {
    id: 6, name: 'Ilhas Maurício',
    description: 'Descubra o paraíso tropical com praias de areia branca, águas cristalinas turquesas e recifes de coral vibrantes. Perfeito para casais e famílias.',
    image: MAURITIUS_IMAGE, duration: '7 dias', groupSize: '1 pessoa', price: 'A partir de R$ 15.000',
    icon: Waves, iconColor: 'text-cyan-600', detailPage: '/mauritius',
  },
];

export default function Destinations() {
  return (
    <section id="destinations" className="py-20 bg-white">
      <div className="container">
        <div className="text-center mb-16 fade-in-up">
          <div className="inline-block px-4 py-2 bg-accent/10 border border-accent/30 rounded-full mb-4">
            <p className="text-accent font-bold text-sm">NOSSOS PACOTES</p>
          </div>
          <h2 className="text-primary mb-4">DESTINOS INCRÍVEIS</h2>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto font-medium">
            Escolha entre nossos pacotes premium e descubra as maravilhas do mundo com conforto e segurança.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 auto-rows-max">
          {destinations.map((destination, index) => {
            const IconComponent = destination.icon;
            return (
              <Link key={destination.id} to={destination.detailPage} className="block card-package group fade-in-up" style={{ animationDelay: `${index * 100}ms` }}>
                <div className="relative h-64 overflow-hidden bg-secondary">
                  <img src={destination.image} alt={destination.name} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110" />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                  {destination.featured && (
                    <div className="absolute top-4 left-4 px-3 py-1 bg-orange-500 text-white text-xs font-bold rounded-full shadow-lg">MAIS PROCURADO</div>
                  )}
                  <div className="absolute top-4 right-4 p-3 bg-white rounded-full shadow-lg group-hover:scale-110 transition-transform duration-300">
                    <IconComponent size={28} className={destination.iconColor} />
                  </div>
                </div>

                <div className="p-6 space-y-4">
                  <div>
                    <div className="flex items-center gap-2 mb-2">
                      <h3 className="text-2xl font-bold text-primary">{destination.name}</h3>
                      <div className="flex gap-1">
                        <Hotel size={18} className="text-accent" />
                        <Plane size={18} className="text-accent" />
                        <Car size={18} className="text-accent" />
                        {(destination.id === 2 || destination.id === 4) && <Binoculars size={18} className="text-orange-600" />}
                      </div>
                    </div>
                    <p className="text-muted-foreground text-sm leading-relaxed">{destination.description}</p>
                  </div>

                  <div className="space-y-3 py-4 border-t border-b border-border">
                    <div className="flex items-center gap-3 text-sm">
                      <Calendar size={18} className="text-accent" />
                      <span className="text-foreground font-semibold">{destination.duration}</span>
                    </div>
                  </div>

                  <button className="w-full btn-secondary inline-flex items-center justify-center gap-2 mt-4">
                    VER ROTEIRO COMPLETO
                    <ArrowRight size={18} />
                  </button>
                </div>
              </Link>
            );
          })}
        </div>
      </div>
    </section>
  );
}
